package de.oevbs.kunde.kundenportal2.components

import dev.fritz2.core.RenderContext

/**
 * Definiert Hilfetexte für Eingabefelder.
 */
object HelpPopovers {

    val username: RenderContext.() -> Unit = {
        listItems(
            "Ihr Benutzername...",
            listOf(
                "wird automatisch in Kleinbuchstaben umgewandelt",
                "muss aus mindestens 4 und maximal 20 Zeichen bestehen",
                "muss mit einem Buchstaben beginnen",
                "darf keine Umlaute (ä, ö, ü..) enthalten",
                "darf keine Leerzeichen enthalten",
                "darf keine Sonderzeichen außer _-.@ enthalten"
            )
        )
    }

    val usernameEmpty: RenderContext.() -> Unit = {
        div("mx-3 my-2") {
            +"Ihren "
            b { +"Benutzernamen" }
            +" finden Sie in der Mail, die Sie nach der Registrierung erhalten haben."
            +" Sollten Sie Ihre Verträge bereits mit Ihrem Account verknüpft haben, können Sie die Funktion"
            +" 'Benutzername vergessen' benutzen, die Sie unter 'Zugangsdaten vergessen' finden, um Ihren"
            +" Benutzernamen zu erfragen."
        }
    }

    val lastname: RenderContext.() -> Unit = {
        listItems(
            "Ihr Nachname...",
            listOf(
                "muss mindestens 2 Zeichen lang sein",
                "darf Umlaute und Leerzeichen enthalten",
                "darf keine Sonderzeichen außer .-'`´ enthalten",
                "darf keine Zahlen enthalten"
            )
        )
    }

    val password: RenderContext.() -> Unit = {
        listItems(
            "Ihr Passwort...",
            listOf(
                "muss 8-20 Zeichen lang sein",
                "muss mindestens eine Ziffer enthalten",
                "muss mindestens einen Großbuchstaben A-Z (ohne ÄÖÜ) enthalten",
                "muss mindestens einen Kleinbuchstaben a-z (ohne äöüß) enthalten",
                "darf zusätzlich Umlaute (ÄÖÜäöüß) enthalten",
                "darf Leerzeichen und Sonderzeichen (außer €) enthalten"
            )
        )
    }

    val street: RenderContext.() -> Unit = {
        listItems(
            "Ihre Straße...",
            listOf(
                "muss mindestens 3 Zeichen lang sein",
                "darf Umlaute und Leerzeichen enthalten",
                "darf keine Sonderzeichen außer -'`´ enthalten"
            )
        )
    }

    val streetNumber: RenderContext.() -> Unit = {
        listItems(
            "Ihre Hausnummer...",
            listOf(
                "muss mit einer Ziffer beginnen",
                "darf nur Ziffern und Buchstaben enthalten",
                "darf keine Sonderzeichen außer - enthalten"
            )
        )
    }

    val postalCode: RenderContext.() -> Unit = {
        div("mx-3 my-2") {
            +"Ihre Postleitzahl muss aus 5 Ziffern bestehen."
        }
    }

    val city: RenderContext.() -> Unit = {
        listItems(
            "Ihr Wohnort...",
            listOf(
                "muss mindestens 2 Zeichen lang sein",
                "darf Umlaute und Leerzeichen enthalten",
                "darf keine Sonderzeichen außer - enthalten"
            )
        )
    }

    val phoneNumber: RenderContext.() -> Unit = {
        listItems(
            "Ihre Telefonnummer...",
            listOf(
                "muss aus 6-13 Ziffern bestehen",
                "darf Leerzeichen enthalten",
                "darf ein voranstehendes + für die Ländervorwahl sowie - und / enthalten",
                "darf keine weiteren Sonderzeichen enthalten"
            )
        )

        separator("secondary-400 mb-1")

        listItems(
            "Beispiele",
            listOf(
                // Beispiele sind von der Bundesnetzagentur ("drama-numbers").
                // Siehe https://en.wikipedia.org/wiki/Fictitious_telephone_number#Germany
                "040 66969000 (Festnetz)",
                "+49 40 66969000 (Festnetz international)",
                "0171 3920000 (Mobil)",
                "+49 171 3920000 (Mobil international)"
            )
        )

        separator("secondary-400 mb-1")

        div {
            b { +"Datenschutzhinweis:" }
            +" Die Hinterlegung Ihrer Telefonnummer ist freiwillig. Sie wird in Ihrer"
            +" Kundenakte gespeichert. Dies können Sie jederzeit für die Zukunft widerrufen. Die Nummer wird nur"
            +" zu werblichen Zwecken verwendet, wenn Sie hierzu eingewilligt haben."
        }
    }

    val policyId: RenderContext.() -> Unit = {
        div("mx-3 my-2") {
            +"Geben Sie Ihre vollständige Vertragsnummer ein (voranstehende Buchstaben und Sonderzeichen müssen nicht"
            +" eingegeben werden). Diese finden Sie in einem "
            b { +"aktuellen Anschreiben" }
            +" von uns. Es können keine Policennummern verwendet werden."
            br {}
            +"Beginnt die Vertragsnummer mit einer 8, so hat sie 13 Stellen."
        }
    }

    val partnerId: RenderContext.() -> Unit = {
        div("mx-3 my-2") {
            +"Ihre "
            b { +"Partnernummer" }
            +" finden Sie auf Ihren Verträgen. Sie besteht aus 1-7 Ziffern ohne voranstehende Nullen."
        }
    }

    val iban: RenderContext.() -> Unit = {
        listItems(
            "Ihre IBAN...",
            listOf(
                "muss mit einem Ländercode beginnen",
                "darf nur Zahlen und Buchstaben enthalten",
                "darf keine Sonderzeichen oder Umlaute enthalten"
            )
        )
    }

    val resetPassUserOrPartner: RenderContext.() -> Unit = {
        div("mx-3 my-2") {
            +"Ihren gewählten "
            b { +"Benutzernamen" }
            +" finden Sie in der Mail, die Sie nach der Registrierung für Meine Öffentliche erhalten haben."
            br {}
            br {}
            +"Ihre "
            b { +"Partnernummer" }
            +" finden Sie auf Ihren Verträgen. Sie besteht aus 1-7 Ziffern ohne voranstehende Nullen."
            +" Die Partnernummer kann allerdings nur dann für die Passwort-Anfrage verwendet werden,"
            +" wenn Sie bereits Verträge mit Ihrem Account verknüpft haben."
        }
    }

    private fun RenderContext.listItems(title: String, items: List<String>) {
        div("mx-3 my-2") {
            div("mb-1") { b { +title } }
            ul("list-disc list-inside") {
                items.forEach {
                    li { +it }
                }
            }
        }
    }
}
